import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, MapPin, ChevronRight } from 'lucide-react';

type Entity = { type: 'org' | 'place'; name: string; province: string | null; count: number };

export default function EntityDirectory() {
  const [q, setQ] = useState('');
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const fetchEntities = (query: string) => {
    setLoading(true);
    axios.get(`/api/entities/search?q=${encodeURIComponent(query)}&limit=60`)
      .then(r => setEntities(r.data.entities))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchEntities(''); }, []);

  const onChange = (val: string) => {
    setQ(val);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchEntities(val.trim()), 250);
  };

  const open = (e: Entity) => {
    const params = new URLSearchParams({ type: e.type, name: e.name });
    if (e.province) params.set('province', e.province);
    navigate(`/entities/profile?${params}`);
  };

  return (
    <div className="bg-transparent min-h-full p-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold text-[color:var(--text-primary)] m-0 mb-1">Entities</h1>
        <p className="text-[13px] text-[color:var(--text-muted)] m-0">
          Look up a municipality, region, or organization and see its natural-asset-management progress over time.
        </p>
      </div>

      <div className="bg-[var(--glass-bg)] backdrop-blur-[12px] border border-[var(--glass-border)] rounded-[16px] p-5 mb-6">
        <div className="flex items-center gap-[10px] bg-[var(--surface)] border border-[var(--border)] rounded-[12px] px-3 py-2">
          <Search size={16} className="text-[color:var(--text-muted)]" />
          <input
            value={q}
            onChange={e => onChange(e.target.value)}
            placeholder="Search towns, cities, or organizations…"
            className="flex-1 bg-transparent outline-none text-[14px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)]"
          />
        </div>
      </div>

      {loading && <p className="text-[13px] text-[color:var(--text-muted)]">Loading…</p>}
      {!loading && entities.length === 0 && (
        <p className="text-[13px] text-[color:var(--text-muted)]">No entities match “{q}”.</p>
      )}

      <div className="grid gap-[10px] [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
        {entities.map((e, i) => (
          <button
            key={`${e.type}-${e.name}-${i}`}
            onClick={() => open(e)}
            className="text-left bg-[var(--glass-bg)] backdrop-blur-[12px] border border-[var(--glass-border)] rounded-[14px] p-4 flex items-center gap-3 hover:border-[color:var(--accent)] transition-colors cursor-pointer"
          >
            <div className="shrink-0 w-9 h-9 rounded-[10px] bg-[var(--surface)] border border-[var(--border)] flex items-center justify-center text-[color:var(--accent)]">
              {e.type === 'org' ? <Building2 size={18} /> : <MapPin size={18} />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-medium text-[color:var(--text-primary)] truncate">{e.name}</div>
              <div className="text-[12px] text-[color:var(--text-muted)] truncate">
                {e.type === 'org' ? 'Organization' : 'Place'}
                {e.province ? ` · ${e.province}` : ''} · {e.count} item{e.count === 1 ? '' : 's'}
              </div>
            </div>
            <ChevronRight size={16} className="text-[color:var(--text-muted)] shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}
